import { get, post, API_URL } from '../utils/api';

/**
 * Fetch the connection fee summary for a site.
 * Returns null if the site has no connection fee configured (404).
 *
 * @param {string} siteUuid - Site UUID (with dashes)
 * @returns {Promise<Object|null>} Summary object or null
 */
export const getConnectionFeeSummary = async (siteUuid) => {
  if (!siteUuid) return null;

  try {
    const response = await get(`${API_URL}/java/cxf/uns/connection-fee/summary/${siteUuid}`);
    const data = response.data;

    // The backend may return 200 with null fee fields when no fee is configured.
    // Treat that as "no connection fee" rather than showing an empty card.
    if (!data || data.feeAmountCents == null) {
      return null;
    }

    return data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    // console.error('Error fetching connection fee summary:', error);
    return null;
  }
};

/**
 * Fetch all connection fee charges for an account.
 * The accountUuid must be in standard UUID format with dashes.
 *
 * @param {string} accountUuid - Account UUID (with dashes)
 * @returns {Promise<Object|null>} Charges response or null on error
 */
export const getConnectionFeeCharges = async (accountUuid) => {
  if (!accountUuid) return null;

  try {
    const response = await get(`${API_URL}/java/cxf/uns/connection-fee/account/${accountUuid}/charges`);
    return response.data;
  } catch (error) {
    // console.error('Error fetching connection fee charges:', error);
    return null;
  }
};

/**
 * Write off an outstanding monthly fixed charge through the website backend.
 * The backend endpoint enforces the admin email allowlist before forwarding to Karaf.
 *
 * @param {Object} payload - Write-off request
 * @param {string} payload.chargeUuid - Monthly fixed charge UUID
 * @param {string} payload.reason - Audit reason
 * @param {string} payload.adjustedBy - Authenticated admin email
 * @returns {Promise<Object|null>} Write-off response data, usually empty
 */
export const writeOffConnectionFeeCharge = async (payload) => {
  const response = await post(`${API_URL}/connection-fee/writeoff`, payload);
  return response.data || null;
};
